export type DonorLinkStatus = 'found' | 'changed' | 'missing';
export type DonorStatus = 'unchecked' | 'found' | 'changed' | 'missing' | 'unavailable' | 'unknown';
export interface DonorExpectedLink {
  id: string;
  target: string;
  anchor: string;
}
export interface DonorFoundLink {
  href: string;
  anchor: string;
  rel: string[];
}
export interface DonorLinkEvidence {
  id: string;
  status: DonorLinkStatus;
  matches: DonorFoundLink[];
}
export type DonorProblemKind =
  | 'http_404'
  | 'http_410'
  | 'all_missing'
  | 'partial_missing'
  | 'blocked'
  | 'rate_limited'
  | 'server_error'
  | 'dns'
  | 'tls'
  | 'timeout'
  | 'network'
  | 'other';
export interface DonorProblem {
  kind: DonorProblemKind | null;
  since: string | null;
  confirmedAt: string | null;
  confirmations: number;
  candidateAt: string | null;
}
export interface DonorArchiveInfo {
  links: DonorExpectedLink[];
  at: string;
  by: string;
  reason: string;
  problem: DonorProblem;
  state: DonorState;
}
export type DonorBulkAction = 'archive' | 'restore' | 'recheck' | 'delete';
export interface DonorFilters {
  search?: string;
  target?: string;
  status?: string;
  scope?: 'active' | 'archived';
  httpStatus?: number;
  problem?: DonorProblemKind;
  problemDays?: number;
  candidates?: 'yes';
}
export interface DonorBulkInput {
  action: DonorBulkAction;
  filter: DonorFilters;
  ids?: string[];
  reason?: string;
}
export interface DonorBulkPreview {
  id: string;
  action: DonorBulkAction;
  total: number;
  reasons: Record<string, number>;
  sample: Array<{ url: string; problem: DonorProblem }>;
  expiresAt: string;
}
export interface DonorBulkResult {
  total: number;
  changed: number;
  skipped: number;
}
export interface DonorActionView {
  id: string;
  action: DonorBulkAction;
  at: string;
  by: string;
  reason: string;
}
export interface DonorState {
  status: DonorStatus;
  lastAttemptAt: string | null;
  lastValidatedAt: string | null;
  lastFullAt: string | null;
  evidence: DonorLinkEvidence[];
  finalUrl: string | null;
  httpStatus: number | null;
  etag: string | null;
  lastModified: string | null;
  validatorsTrusted: boolean;
  validatorWarning: string | null;
  bodyHash: string | null;
  fingerprint: string | null;
  stableChecks: number;
  failures: number;
  intervalDays: number;
  error: string | null;
  errorKind?: DonorProblemKind | null;
  method: 'full' | 'conditional' | null;
  bytes: number;
}
export interface DonorArticleView {
  id: string;
  url: string;
  sourceInput: string;
  sourceHost: string;
  note: string;
  archived: boolean;
  revision: number;
  problem: DonorProblem;
  archive: DonorArchiveInfo | null;
  links: DonorExpectedLink[];
  state: DonorState;
  nextCheckAt: string;
  queued: boolean;
  deferredUntil: string | null;
  deferredReason: string | null;
}
export interface DonorList {
  items: DonorArticleView[];
  total: number;
  page: number;
  stats: { total: number; due: number; statuses: Record<string, number> };
  paused: boolean;
  worker: { online: boolean; lastSeenAt: string | null; error: string | null };
}
export interface DonorImportPreview {
  rows: number;
  articles: number;
  empty: number;
  duplicates: number;
  mergedRows: number;
  errors: Array<{ line: number; message: string }>;
  sample: Array<{ domain: string; target: string; anchor: string }>;
}
export type DonorCsvDelimiter = ',' | ';' | '\t' | '|';
export interface DonorCsvOptions {
  delimiter: DonorCsvDelimiter;
  hasHeader: boolean;
  columns: { article: number; target: number; anchor?: number | null };
}
export interface DonorCsvSampleInput {
  sample: string;
  complete: boolean;
  delimiter?: DonorCsvDelimiter;
  hasHeader?: boolean;
}
export interface DonorCsvSample {
  delimiter: DonorCsvDelimiter;
  hasHeader: boolean;
  columns: Array<{ index: number; name: string; examples: string[] }>;
  suggested: { article: number | null; target: number | null; anchor: number | null };
  sampledRows: number;
}
export interface DonorImportResult {
  added: number;
  updated: number;
  unchanged: number;
  archivedSkipped: number;
  empty: number;
  duplicates: number;
  mergedRows: number;
}
export interface DonorImportJob extends DonorImportResult {
  id: string;
  fileName: string;
  total: number;
  processed: number;
  status: 'queued' | 'running' | 'retrying' | 'completed' | 'failed';
  error: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface DonorLookupItem {
  id: string;
  url: string;
  archived: boolean;
  exact: boolean;
}
export interface DonorHistoryItem {
  id: string;
  checkedAt: string;
  changed: boolean;
  state: DonorState;
}
export function emptyDonorState(): DonorState {
  return {
    status: 'unchecked',
    lastAttemptAt: null,
    lastValidatedAt: null,
    lastFullAt: null,
    evidence: [],
    finalUrl: null,
    httpStatus: null,
    etag: null,
    lastModified: null,
    validatorsTrusted: true,
    validatorWarning: null,
    bodyHash: null,
    fingerprint: null,
    stableChecks: 0,
    failures: 0,
    intervalDays: 1,
    error: null,
    method: null,
    bytes: 0,
  };
}

export const PROBLEM_LABELS: Record<DonorProblemKind, string> = {
  http_404: 'HTTP 404',
  http_410: 'HTTP 410',
  all_missing: 'Все ссылки отсутствуют',
  partial_missing: 'Часть ссылок отсутствует',
  blocked: 'Доступ заблокирован / капча',
  rate_limited: 'Слишком много запросов',
  server_error: 'Ошибка сервера',
  dns: 'Ошибка DNS',
  tls: 'Ошибка TLS',
  timeout: 'Таймаут',
  network: 'Сетевая ошибка',
  other: 'Другая ошибка',
};

export const DONOR_CANDIDATE_RULES: Partial<
  Record<DonorProblemKind, { days: number; count: number }>
> = {
  http_404: { days: 7, count: 3 },
  http_410: { days: 1, count: 2 },
  all_missing: { days: 7, count: 3 },
};
export const DONOR_CONFIRMATION_GAP_HOURS = 24;
export const DONOR_CANDIDATE_FRESH_HOURS = 36;
